import { DataTableFilterMetaData } from 'primevue/datatable';

// PrimeVue's FilterMatchMode values are lowercase/camelCase strings (e.g. "equals",
// "contains", "startsWith"). The API's QueryFilter.OperatorType is PascalCase and has
// exactly these members: StartsWith, Contains, NotContains, EndsWith, Equals, NotEquals,
// Lt, Lte, Gt, Gte. An operator name the API can't parse is coerced to Contains server-side,
// which throws "Unsupported operator: Contains" for every non-string field — so the map only
// ever emits names that exist. The date match modes are deliberately absent: they expand to
// Gte/Lt pairs in toDateFilters below, because the API has no DateIs / Between operator.
const matchModeToOperator: Record<string, string> = {
    contains: 'Contains',
    notContains: 'NotContains',
    startsWith: 'StartsWith',
    endsWith: 'EndsWith',
    equals: 'Equals',
    notEquals: 'NotEquals',
    lt: 'Lt',
    lte: 'Lte',
    gt: 'Gt',
    gte: 'Gte',
};

/** PrimeVue match modes whose constraint value is a Date (or a [start, end] range of them). */
const dateMatchModes = new Set(['dateIs', 'dateBefore', 'dateAfter', 'between']);

/** One `{ field, operator, value }` entry as the API's `filters` query parameter expects it. */
export type ApiQueryFilter = { field: string; operator: string; value: string };

const toDate = (value: unknown): Date | null => {
    if (value instanceof Date) return isNaN(value.getTime()) ? null : value;
    if (typeof value === 'string' && value.trim() !== '') {
        const parsed = new Date(value);
        return isNaN(parsed.getTime()) ? null : parsed;
    }
    return null;
};

const addDays = (date: Date, days: number): Date => {
    const next = new Date(date);
    next.setDate(next.getDate() + days);
    return next;
};

// Local calendar date, not toISOString(): the picker hands back local midnight, and an
// evening pick in the Americas would otherwise serialise as the next UTC day.
const toDateOnly = (date: Date): string => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
};

/**
 * Expands a date-picker constraint into the half-open day range the API can evaluate.
 *
 * `QueryFilter.OperatorType` has only comparison operators for dates (`Lt`, `Lte`, `Gt`, `Gte`),
 * and `cleanDataTableFilters` sends one constraint per field, so "orders on 30 Sep" has to
 * travel as two filters on the same field: `Gte 2026-09-30` and `Lt 2026-10-01`. Dates go out
 * as `yyyy-MM-dd`, one of the formats `QueryFilterParser.ParseDate` accepts, which the parser
 * then binds as midnight.
 *
 * A range with no end yet (`[start, null]`, the picker's state after the first click) is
 * treated as that single day, so applying it from the filter menu does what it looks like.
 * Anything that isn't a usable date yields no filter at all rather than a text operator on a
 * date field, which is what made the old free-text Order Date filter error out.
 */
export const toDateFilters = (field: string, matchMode: string, value: unknown): ApiQueryFilter[] => {
    const parts = Array.isArray(value) ? value : [value];
    const start = toDate(parts[0]);
    if (!start) return [];

    switch (matchMode) {
        case 'dateBefore':
            return [{ field, operator: 'Lt', value: toDateOnly(start) }];
        case 'dateAfter':
            return [{ field, operator: 'Gte', value: toDateOnly(addDays(start, 1)) }];
        case 'dateIs':
        case 'between':
        default: {
            const end = (Array.isArray(value) && toDate(parts[1])) || start;
            const [from, to] = end < start ? [end, start] : [start, end];
            return [
                { field, operator: 'Gte', value: toDateOnly(from) },
                { field, operator: 'Lt', value: toDateOnly(addDays(to, 1)) },
            ];
        }
    }
};

export class PagingParameters {
    first: number;
    rows: number;
    sortField: string;
    sortOrder: number;
    filters: DataTableFilterMetaData[];
    rowsPerPageOptions: number[] = [25, 50, 100, 200];
    /**
     * Filters a link brought the list in with ("overdue", "stale") that no column's filter menu
     * can express — several on one field, or a cut-off relative to today. Sent with the column
     * filters; the page shows them in a banner and clears them there.
     */
    linkedFilters: ApiQueryFilter[] = [];
    /**
     * Filters the page itself adds beside the column filters and a link's — a toolbar view such as
     * "open tasks only", or a due window whose instants no column filter can carry. Sent with the
     * rest; the page owns and rebuilds them.
     */
    extraFilters: ApiQueryFilter[] = [];

    constructor(sortField: string, sortOrder: number = 1) {
        this.first = 0;
        this.rows = 25;
        this.sortField = sortField; // Default sort field
        this.sortOrder = sortOrder;
        this.filters = [];
    }
    
    MapEventToPagingParameters(event: any): void {
        this.rows = event.rows;
        this.first = event.first;
        this.sortField = event.sortField;
        this.sortOrder = event.sortOrder;
        this.filters = event.filters;
    }
    
    get CleanedFilters(): ApiQueryFilter[] | undefined {
        return [...this.cleanDataTableFilters(this.filters), ...(this.linkedFilters ?? []), ...(this.extraFilters ?? [])];
    }

    // Image presence travels as the dedicated hasImages query param, never as a QueryFilter:
    // the API coerces filter operators to Contains, which cannot express array presence.
    get HasImages(): boolean | undefined {
        const value = (this.filters as any)?.images?.constraints?.[0]?.value;
        return typeof value === 'boolean' ? value : undefined;
    }

    // Tags travel as the dedicated tag query param for the same reason images do — tags are an
    // array, and a Contains filter cannot express membership. The API matches on the normalized
    // UPPERCASE tag, so the value here is case-insensitive.
    get Tag(): string | undefined {
        const value = (this.filters as any)?.tag?.constraints?.[0]?.value;
        return typeof value === 'string' && value.trim() !== '' ? value : undefined;
    }
    
    get PageNumber(): number {
        return this.first / this.rows + 1;
    }
    
    get SortFieldProperCase(): string {
        if (!this.sortField) return '';
        // Handle nested fields like 'customer.companyName'
        if (this.sortField.includes('.')) {
            const parts = this.sortField.split('.');
            return parts.map(part => part.charAt(0).toUpperCase() + part.slice(1)).join('.');
        }
        return this.sortField.charAt(0).toUpperCase() + this.sortField.slice(1);
    }
    
    get SortOrderAsString(): string {
        return this.sortOrder === 1 ? 'asc' : 'desc';
    }

    cleanDataTableFilters(input: any): ApiQueryFilter[] {
        if (!input || typeof input !== 'object') {
            return [];
        }

        const output: ApiQueryFilter[] = [];

        for (const key in input) {
            // Handled by the HasImages / Tag getters above; as QueryFilters they would be rejected.
            if (key === 'images' || key === 'tag') {
                continue;
            }
            if (input[key]?.constraints?.length > 0) {
                const constraint = input[key].constraints[0];
                const matchMode = constraint?.matchMode;
                const value = constraint?.value;

                // A date picker's value is a Date, or a [start, end] pair of them, never text.
                if (dateMatchModes.has(matchMode) || value instanceof Date || Array.isArray(value)) {
                    output.push(...toDateFilters(key, matchMode, value));
                    continue;
                }

                if (value != null && value !== '') {
                    output.push({
                        field: key,
                        operator: matchModeToOperator[matchMode] ?? matchMode,
                        value: String(value)
                    });
                }
            }
        }

        return output.filter(filter => 
            filter.value != null && 
            filter.value !== '' && 
            filter.field != null
        );
    }
}
